import { useState, useCallback, useRef } from 'react';
import {
  fashionCollection,
  fashionCollectionExtra,
  swimwearCollection,
  sportswearCollection,
  bodyCollection,
  setsCollection,
  cropTopCollection,
  jeansCollection,
  vestidosCollection,
  FashionProduct,
  SwimwearProduct,
  SportswearProduct,
  BodyProduct,
  SetProduct,
  CropTopProduct,
  JeanProduct,
  VestidoProduct,
} from '@/mocks/products';

const WA_BASE = 'https://wa.me/573228000501?text=Hola%2C%20me%20interesa%20la%20prenda%20';

type TabKey = 'bano' | 'deportiva' | 'bodies' | 'sets' | 'croptops' | 'jeans' | 'vestidos';

function StarRating({ count }: { count: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <i key={i} className={`${i < count ? 'ri-star-fill' : 'ri-star-line'} text-gold text-sm`} />
      ))}
    </div>
  );
}

function FashionCard({ product }: { product: FashionProduct }) {
  return (
    <article className="bg-white rounded-2xl overflow-hidden hover:-translate-y-2 transition-all duration-300 group flex flex-col">
      <div className="relative aspect-[4/5.5] overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          title={`${product.name} - Moda femenina exclusiva LauraSoto_Shop`}
          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-3 right-3 bg-gold text-white text-[10px] font-sans font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
          {product.category}
        </div>
        {product.isBestSeller && (
          <div className="absolute top-3 left-3 bg-charcoal text-white text-[10px] font-sans font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
            ⭐ Más vendido
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-sans font-semibold text-sm md:text-base text-charcoal mb-1">
          {product.name}
        </h3>
        <p className="font-sans text-xs text-gray-medium mb-3 flex-1">
          {product.description}
        </p>
        <div className="flex items-center gap-2 mb-3">
          <StarRating count={product.rating} />
          <span className="font-sans text-xs text-gray-medium">({product.reviews})</span>
        </div>
        <p className="font-serif font-bold text-lg md:text-xl text-charcoal mb-4">
          <span className="text-gold">$</span>{product.price.replace('$', '')}
        </p>
        <a
          href={`${WA_BASE}${encodeURIComponent(product.name)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white py-3 px-4 rounded-xl font-sans text-xs md:text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer hover:scale-[1.02]"
        >
          <i className="ri-whatsapp-fill text-base md:text-lg" />
          Consultar por WhatsApp
        </a>
        <p className="font-sans text-[10px] text-gray-medium text-center mt-2">
          Compra fácil escribiéndonos por WhatsApp
        </p>
      </div>
    </article>
  );
}

function CarouselCard({ product }: { product: SwimwearProduct | SportswearProduct }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goNext = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex((prev) => (prev + 1) % product.images.length);
    },
    [product.images.length],
  );

  const goPrev = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
    },
    [product.images.length],
  );

  const goTo = useCallback((idx: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(idx);
  }, []);

  return (
    <article className="bg-white rounded-2xl overflow-hidden hover:-translate-y-2 transition-all duration-300 group flex flex-col">
      <div className="relative aspect-[4/5.5] overflow-hidden bg-gray-50">
        {product.images.map((img, idx) => (
          <img
            key={idx}
            src={img}
            alt={`${product.name} - vista ${idx + 1}`}
            title={`${product.name} - LauraSoto_Shop`}
            className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500 ${
              idx === currentIndex ? 'opacity-100' : 'opacity-0'
            }`}
            loading="lazy"
          />
        ))}

        <div className="absolute top-3 right-3 bg-gold text-white text-[10px] font-sans font-semibold px-2.5 py-1 rounded-full whitespace-nowrap z-10">
          {product.category}
        </div>
        {product.isBestSeller && (
          <div className="absolute top-3 left-3 bg-charcoal text-white text-[10px] font-sans font-semibold px-2.5 py-1 rounded-full whitespace-nowrap z-10">
            ⭐ Más vendido
          </div>
        )}

        <button
          onClick={goPrev}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 z-10 cursor-pointer"
          aria-label="Foto anterior"
        >
          <i className="ri-arrow-left-s-line text-charcoal text-base" />
        </button>
        <button
          onClick={goNext}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 z-10 cursor-pointer"
          aria-label="Foto siguiente"
        >
          <i className="ri-arrow-right-s-line text-charcoal text-base" />
        </button>

        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
          {product.images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => goTo(idx, e)}
              className={`rounded-full transition-all duration-300 cursor-pointer ${
                idx === currentIndex
                  ? 'w-4 h-1.5 bg-gold'
                  : 'w-1.5 h-1.5 bg-white/70 hover:bg-white'
              }`}
              aria-label={`Ver foto ${idx + 1}`}
            />
          ))}
        </div>

        <div className="absolute bottom-3 right-3 bg-black/40 text-white text-[10px] font-sans px-2 py-0.5 rounded-full z-10">
          {currentIndex + 1}/{product.images.length}
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-sans font-semibold text-sm md:text-base text-charcoal mb-1">
          {product.name}
        </h3>
        <p className="font-sans text-xs text-gray-medium mb-3 flex-1">
          {product.description}
        </p>
        <div className="flex items-center gap-2 mb-3">
          <StarRating count={product.rating} />
          <span className="font-sans text-xs text-gray-medium">({product.reviews})</span>
        </div>
        <div className="mb-4">
          {(product as SwimwearProduct).originalPrice && (
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-sans text-sm text-gray-400 line-through">{(product as SwimwearProduct).originalPrice}</span>
              <span className="bg-red-100 text-red-600 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">{(product as SwimwearProduct).discount}</span>
            </div>
          )}
          <p className={`font-serif font-bold text-lg md:text-xl ${(product as SwimwearProduct).originalPrice ? 'text-red-600' : 'text-charcoal'}`}>
            <span className={(product as SwimwearProduct).originalPrice ? 'text-red-500' : 'text-gold'}>$</span>{product.price.replace('$', '')}
          </p>
        </div>
        <a
          href={`${WA_BASE}${encodeURIComponent(product.name)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white py-3 px-4 rounded-xl font-sans text-xs md:text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer hover:scale-[1.02]"
        >
          <i className="ri-whatsapp-fill text-base md:text-lg" />
          Consultar por WhatsApp
        </a>
        <p className="font-sans text-[10px] text-gray-medium text-center mt-2">
          Compra fácil escribiéndonos por WhatsApp
        </p>
      </div>
    </article>
  );
}

function BodyCard({ product }: { product: BodyProduct }) {
  return (
    <article className="bg-white rounded-2xl overflow-hidden hover:-translate-y-2 transition-all duration-300 flex flex-col">
      <div className="relative aspect-[4/5.5] overflow-hidden bg-gray-50">
        <img
          src={product.image}
          alt={`${product.name} - Body LauraSoto_Shop`}
          title={`${product.name} - Bodies LauraSoto_Shop`}
          className="w-full h-full object-cover object-top"
          loading="lazy"
        />
        <div className="absolute top-3 right-3 bg-gold text-white text-[10px] font-sans font-semibold px-2.5 py-1 rounded-full whitespace-nowrap z-10">
          {product.category}
        </div>
        {product.isBestSeller && (
          <div className="absolute top-3 left-3 bg-charcoal text-white text-[10px] font-sans font-semibold px-2.5 py-1 rounded-full whitespace-nowrap z-10">
            ⭐ Más vendido
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-sans font-semibold text-sm md:text-base text-charcoal mb-1">
          {product.name}
        </h3>
        <p className="font-sans text-xs text-gray-medium mb-3 flex-1">
          {product.description}
        </p>
        <div className="flex items-center gap-2 mb-3">
          <StarRating count={product.rating} />
          <span className="font-sans text-xs text-gray-medium">({product.reviews})</span>
        </div>
        <p className="font-serif font-bold text-lg md:text-xl text-charcoal mb-4">
          <span className="text-gold">$</span>{product.price.replace('$', '')}
        </p>
        <a
          href={`${WA_BASE}${encodeURIComponent(product.name)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white py-3 px-4 rounded-xl font-sans text-xs md:text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer hover:scale-[1.02]"
        >
          <i className="ri-whatsapp-fill text-base md:text-lg" />
          Consultar por WhatsApp
        </a>
        <p className="font-sans text-[10px] text-gray-medium text-center mt-2">
          Compra fácil escribiéndonos por WhatsApp
        </p>
      </div>
    </article>
  );
}

function SetCard({ product }: { product: SetProduct }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  const goNext = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex((prev) => (prev + 1) % product.media.length);
    },
    [product.media.length],
  );

  const goPrev = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex((prev) => (prev - 1 + product.media.length) % product.media.length);
    },
    [product.media.length],
  );

  const goTo = useCallback((idx: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(idx);
  }, []);

  const currentMedia = product.media[currentIndex];

  return (
    <article className="bg-white rounded-2xl overflow-hidden hover:-translate-y-2 transition-all duration-300 group flex flex-col">
      <div className="relative aspect-[4/5.5] overflow-hidden bg-gray-50">
        {product.media.map((item, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-opacity duration-500 ${
              idx === currentIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            {item.type === 'video' ? (
              <video
                ref={idx === currentIndex ? videoRef : undefined}
                src={item.url}
                poster={item.poster}
                className="w-full h-full object-cover object-top"
                autoPlay
                muted
                loop
                playsInline
                aria-label={`${product.name} - video`}
              />
            ) : (
              <img
                src={item.url}
                alt={`${product.name} - vista ${idx + 1}`}
                title={`${product.name} - Sets LauraSoto_Shop`}
                className="w-full h-full object-cover object-top"
                loading="lazy"
              />
            )}
          </div>
        ))}

        {currentMedia.type === 'video' && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-black/50 text-white text-[10px] font-sans px-2.5 py-1 rounded-full flex items-center gap-1 z-10 whitespace-nowrap">
            <i className="ri-play-circle-fill text-xs" />
            Video
          </div>
        )}

        <div className="absolute top-3 right-3 bg-gold text-white text-[10px] font-sans font-semibold px-2.5 py-1 rounded-full whitespace-nowrap z-10">
          {product.category}
        </div>
        {product.isBestSeller && (
          <div className="absolute top-3 left-3 bg-charcoal text-white text-[10px] font-sans font-semibold px-2.5 py-1 rounded-full whitespace-nowrap z-10">
            ⭐ Más vendido
          </div>
        )}

        {product.media.length > 1 && (
          <>
            <button
              onClick={goPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 z-10 cursor-pointer"
              aria-label="Anterior"
            >
              <i className="ri-arrow-left-s-line text-charcoal text-base" />
            </button>
            <button
              onClick={goNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 z-10 cursor-pointer"
              aria-label="Siguiente"
            >
              <i className="ri-arrow-right-s-line text-charcoal text-base" />
            </button>
          </>
        )}

        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
          {product.media.map((item, idx) => (
            <button
              key={idx}
              onClick={(e) => goTo(idx, e)}
              className={`rounded-full transition-all duration-300 cursor-pointer ${
                idx === currentIndex
                  ? 'w-4 h-1.5 bg-gold'
                  : 'w-1.5 h-1.5 bg-white/70 hover:bg-white'
              }`}
              aria-label={item.type === 'video' ? 'Ver video' : `Ver foto ${idx + 1}`}
            />
          ))}
        </div>

        <div className="absolute bottom-3 right-3 bg-black/40 text-white text-[10px] font-sans px-2 py-0.5 rounded-full z-10">
          {currentIndex + 1}/{product.media.length}
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-sans font-semibold text-sm md:text-base text-charcoal mb-1">
          {product.name}
        </h3>
        <p className="font-sans text-xs text-gray-medium mb-3 flex-1">
          {product.description}
        </p>
        <div className="flex items-center gap-2 mb-3">
          <StarRating count={product.rating} />
          <span className="font-sans text-xs text-gray-medium">({product.reviews})</span>
        </div>
        <p className="font-serif font-bold text-lg md:text-xl text-charcoal mb-4">
          <span className="text-gold">$</span>{product.price.replace('$', '')}
        </p>
        <a
          href={`${WA_BASE}${encodeURIComponent(product.name)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white py-3 px-4 rounded-xl font-sans text-xs md:text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer hover:scale-[1.02]"
        >
          <i className="ri-whatsapp-fill text-base md:text-lg" />
          Consultar por WhatsApp
        </a>
        <p className="font-sans text-[10px] text-gray-medium text-center mt-2">
          Compra fácil escribiéndonos por WhatsApp
        </p>
      </div>
    </article>
  );
}

function CropTopCard({ product }: { product: CropTopProduct }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goNext = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex((prev) => (prev + 1) % product.images.length);
    },
    [product.images.length],
  );

  const goPrev = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
    },
    [product.images.length],
  );

  const goTo = useCallback((idx: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(idx);
  }, []);

  const hasMultiple = product.images.length > 1;

  return (
    <article className="bg-white rounded-2xl overflow-hidden hover:-translate-y-2 transition-all duration-300 group flex flex-col">
      <div className="relative aspect-[4/5.5] overflow-hidden bg-gray-50">
        {product.images.map((img, idx) => (
          <img
            key={idx}
            src={img}
            alt={`${product.name} - vista ${idx + 1}`}
            title={`${product.name} - Crop Tops LauraSoto_Shop`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              idx === currentIndex ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ objectPosition: product.objectPosition ?? 'top' }}
            loading="lazy"
          />
        ))}

        <div className="absolute top-3 right-3 bg-gold text-white text-[10px] font-sans font-semibold px-2.5 py-1 rounded-full whitespace-nowrap z-10">
          {product.category}
        </div>
        {product.isBestSeller && (
          <div className="absolute top-3 left-3 bg-charcoal text-white text-[10px] font-sans font-semibold px-2.5 py-1 rounded-full whitespace-nowrap z-10">
            ⭐ Más vendido
          </div>
        )}

        {hasMultiple && (
          <>
            <button
              onClick={goPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 z-10 cursor-pointer"
              aria-label="Foto anterior"
            >
              <i className="ri-arrow-left-s-line text-charcoal text-base" />
            </button>
            <button
              onClick={goNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 z-10 cursor-pointer"
              aria-label="Foto siguiente"
            >
              <i className="ri-arrow-right-s-line text-charcoal text-base" />
            </button>

            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
              {product.images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => goTo(idx, e)}
                  className={`rounded-full transition-all duration-300 cursor-pointer ${
                    idx === currentIndex
                      ? 'w-4 h-1.5 bg-gold'
                      : 'w-1.5 h-1.5 bg-white/70 hover:bg-white'
                  }`}
                  aria-label={`Ver foto ${idx + 1}`}
                />
              ))}
            </div>

            <div className="absolute bottom-3 right-3 bg-black/40 text-white text-[10px] font-sans px-2 py-0.5 rounded-full z-10">
              {currentIndex + 1}/{product.images.length}
            </div>
          </>
        )}
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-sans font-semibold text-sm md:text-base text-charcoal mb-1">
          {product.name}
        </h3>
        <p className="font-sans text-xs text-gray-medium mb-3 flex-1">
          {product.description}
        </p>
        <div className="flex items-center gap-2 mb-3">
          <StarRating count={product.rating} />
          <span className="font-sans text-xs text-gray-medium">({product.reviews})</span>
        </div>
        <div className="mb-4">
          {product.originalPrice && (
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-sans text-sm text-gray-400 line-through">{product.originalPrice}</span>
              <span className="bg-red-100 text-red-600 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">{product.discount}</span>
            </div>
          )}
          <p className={`font-serif font-bold text-lg md:text-xl ${product.originalPrice ? 'text-red-600' : 'text-charcoal'}`}>
            <span className={product.originalPrice ? 'text-red-500' : 'text-gold'}>$</span>{product.price.replace('$', '')}
          </p>
        </div>
        <a
          href={`${WA_BASE}${encodeURIComponent(product.name)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white py-3 px-4 rounded-xl font-sans text-xs md:text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer hover:scale-[1.02]"
        >
          <i className="ri-whatsapp-fill text-base md:text-lg" />
          Consultar por WhatsApp
        </a>
        <p className="font-sans text-[10px] text-gray-medium text-center mt-2">
          Compra fácil escribiéndonos por WhatsApp
        </p>
      </div>
    </article>
  );
}

function JeanCard({ product }: { product: JeanProduct }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goNext = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex((prev) => (prev + 1) % product.images.length);
    },
    [product.images.length],
  );

  const goPrev = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
    },
    [product.images.length],
  );

  const goTo = useCallback((idx: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(idx);
  }, []);

  const hasMultiple = product.images.length > 1;

  return (
    <article className="bg-white rounded-2xl overflow-hidden hover:-translate-y-2 transition-all duration-300 group flex flex-col">
      <div className="relative aspect-[4/5.5] overflow-hidden bg-gray-50">
        {product.images.map((img, idx) => (
          <img
            key={idx}
            src={img}
            alt={`${product.name} - vista ${idx + 1}`}
            title={`${product.name} - Jeans LauraSoto_Shop`}
            className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500 ${
              idx === currentIndex ? 'opacity-100' : 'opacity-0'
            }`}
            loading="lazy"
          />
        ))}

        <div className="absolute top-3 right-3 bg-gold text-white text-[10px] font-sans font-semibold px-2.5 py-1 rounded-full whitespace-nowrap z-10">
          {product.category}
        </div>
        {product.isBestSeller && (
          <div className="absolute top-3 left-3 bg-charcoal text-white text-[10px] font-sans font-semibold px-2.5 py-1 rounded-full whitespace-nowrap z-10">
            ⭐ Más vendido
          </div>
        )}

        {hasMultiple && (
          <>
            <button
              onClick={goPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 z-10 cursor-pointer"
              aria-label="Foto anterior"
            >
              <i className="ri-arrow-left-s-line text-charcoal text-base" />
            </button>
            <button
              onClick={goNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 z-10 cursor-pointer"
              aria-label="Foto siguiente"
            >
              <i className="ri-arrow-right-s-line text-charcoal text-base" />
            </button>

            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
              {product.images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => goTo(idx, e)}
                  className={`rounded-full transition-all duration-300 cursor-pointer ${
                    idx === currentIndex
                      ? 'w-4 h-1.5 bg-gold'
                      : 'w-1.5 h-1.5 bg-white/70 hover:bg-white'
                  }`}
                  aria-label={`Ver foto ${idx + 1}`}
                />
              ))}
            </div>

            <div className="absolute bottom-3 right-3 bg-black/40 text-white text-[10px] font-sans px-2 py-0.5 rounded-full z-10">
              {currentIndex + 1}/{product.images.length}
            </div>
          </>
        )}
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-sans font-semibold text-sm md:text-base text-charcoal mb-1">
          {product.name}
        </h3>
        <p className="font-sans text-xs text-gray-medium mb-3 flex-1">
          {product.description}
        </p>
        <div className="flex items-center gap-2 mb-3">
          <StarRating count={product.rating} />
          <span className="font-sans text-xs text-gray-medium">({product.reviews})</span>
        </div>
        <p className="font-serif font-bold text-lg md:text-xl text-charcoal mb-4">
          <span className="text-gold">$</span>{product.price.replace('$', '')}
        </p>
        <a
          href={`${WA_BASE}${encodeURIComponent(product.name)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white py-3 px-4 rounded-xl font-sans text-xs md:text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer hover:scale-[1.02]"
        >
          <i className="ri-whatsapp-fill text-base md:text-lg" />
          Consultar por WhatsApp
        </a>
        <p className="font-sans text-[10px] text-gray-medium text-center mt-2">
          Compra fácil escribiéndonos por WhatsApp
        </p>
      </div>
    </article>
  );
}

function VestidoCard({ product }: { product: VestidoProduct }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goNext = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex((prev) => (prev + 1) % product.images.length);
    },
    [product.images.length],
  );

  const goPrev = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
    },
    [product.images.length],
  );

  const goTo = useCallback((idx: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(idx);
  }, []);

  const hasMultiple = product.images.length > 1;

  return (
    <article className="bg-white rounded-2xl overflow-hidden hover:-translate-y-2 transition-all duration-300 group flex flex-col">
      <div className="relative aspect-[4/5.5] overflow-hidden bg-gray-50">
        {product.images.map((img, idx) => (
          <img
            key={idx}
            src={img}
            alt={`${product.name} - vista ${idx + 1}`}
            title={`${product.name} - Vestidos LauraSoto_Shop`}
            className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500 ${
              idx === currentIndex ? 'opacity-100' : 'opacity-0'
            }`}
            loading="lazy"
          />
        ))}

        <div className="absolute top-3 right-3 bg-gold text-white text-[10px] font-sans font-semibold px-2.5 py-1 rounded-full whitespace-nowrap z-10">
          {product.category}
        </div>
        {product.isBestSeller && (
          <div className="absolute top-3 left-3 bg-charcoal text-white text-[10px] font-sans font-semibold px-2.5 py-1 rounded-full whitespace-nowrap z-10">
            ⭐ Más vendido
          </div>
        )}

        {hasMultiple && (
          <>
            <button
              onClick={goPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 z-10 cursor-pointer"
              aria-label="Foto anterior"
            >
              <i className="ri-arrow-left-s-line text-charcoal text-base" />
            </button>
            <button
              onClick={goNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 z-10 cursor-pointer"
              aria-label="Foto siguiente"
            >
              <i className="ri-arrow-right-s-line text-charcoal text-base" />
            </button>

            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
              {product.images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => goTo(idx, e)}
                  className={`rounded-full transition-all duration-300 cursor-pointer ${
                    idx === currentIndex
                      ? 'w-4 h-1.5 bg-gold'
                      : 'w-1.5 h-1.5 bg-white/70 hover:bg-white'
                  }`}
                  aria-label={`Ver foto ${idx + 1}`}
                />
              ))}
            </div>

            <div className="absolute bottom-3 right-3 bg-black/40 text-white text-[10px] font-sans px-2 py-0.5 rounded-full z-10">
              {currentIndex + 1}/{product.images.length}
            </div>
          </>
        )}
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-sans font-semibold text-sm md:text-base text-charcoal mb-1">
          {product.name}
        </h3>
        <p className="font-sans text-xs text-gray-medium mb-3 flex-1">
          {product.description}
        </p>
        <div className="flex items-center gap-2 mb-3">
          <StarRating count={product.rating} />
          <span className="font-sans text-xs text-gray-medium">({product.reviews})</span>
        </div>
        <div className="mb-4">
          {product.originalPrice && (
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-sans text-sm text-gray-400 line-through">{product.originalPrice}</span>
              <span className="bg-red-100 text-red-600 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">{product.discount}</span>
            </div>
          )}
          <p className={`font-serif font-bold text-lg md:text-xl ${product.originalPrice ? 'text-red-600' : 'text-charcoal'}`}>
            <span className={product.originalPrice ? 'text-red-500' : 'text-gold'}>$</span>{product.price.replace('$', '')}
          </p>
        </div>
        <a
          href={`${WA_BASE}${encodeURIComponent(product.name)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white py-3 px-4 rounded-xl font-sans text-xs md:text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer hover:scale-[1.02]"
        >
          <i className="ri-whatsapp-fill text-base md:text-lg" />
          Consultar por WhatsApp
        </a>
        <p className="font-sans text-[10px] text-gray-medium text-center mt-2">
          Compra fácil escribiéndonos por WhatsApp
        </p>
      </div>
    </article>
  );
}

const TABS: { key: TabKey; label: string; icon: string; count: number }[] = [
  {
    key: 'bano',
    label: 'Trajes de Baño',
    icon: 'ri-sun-line',
    count: swimwearCollection.length,
  },
  {
    key: 'deportiva',
    label: 'Ropa Deportiva',
    icon: 'ri-run-line',
    count: sportswearCollection.length,
  },
  {
    key: 'bodies',
    label: 'Bodies',
    icon: 'ri-body-scan-line',
    count: bodyCollection.length,
  },
  {
    key: 'sets',
    label: 'Sets',
    icon: 'ri-layout-grid-line',
    count: setsCollection.length,
  },
  {
    key: 'croptops',
    label: 'Crop Tops',
    icon: 'ri-scissors-cut-line',
    count: cropTopCollection.length,
  },
  {
    key: 'jeans',
    label: 'Jeans',
    icon: 'ri-shirt-line',
    count: jeansCollection.length,
  },
  {
    key: 'vestidos',
    label: 'Vestidos',
    icon: 'ri-women-line',
    count: vestidosCollection.length,
  },
];

export default function FashionCollection() {
  const [activeTab, setActiveTab] = useState<TabKey>('bano');

  const handleTabChange = (key: TabKey) => {
    setActiveTab(key);
  };

  return (
    <section id="ropa" className="py-20 px-6 bg-sand/20">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block font-sans text-xs tracking-[0.25em] text-gold uppercase mb-3">
            Nueva Temporada
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-charcoal mb-4">
            Colección de Moda Femenina
          </h2>
          <div className="w-10 h-0.5 bg-gold mx-auto mb-4" />
          <p className="font-sans text-sm md:text-base text-gray-medium">
            Prendas exclusivas que resaltan tu personalidad
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`flex items-center gap-2 px-5 py-3 rounded-full font-sans text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-charcoal text-white'
                  : 'bg-white text-charcoal border border-stone-200 hover:bg-stone-50'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${tab.icon} text-sm`} />
              </div>
              {tab.label}
              <span
                className={`text-xs px-1.5 py-0.5 rounded-full font-sans ${
                  activeTab === tab.key
                    ? 'bg-white/20 text-white'
                    : 'bg-stone-100 text-stone-500'
                }`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Grid — Trajes de Baño */}
        {activeTab === 'bano' && (
          <>
            <div
              className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
              data-product-shop={true}
            >
              {swimwearCollection.map((p) => (
                <CarouselCard key={p.id} product={p} />
              ))}
            </div>
            <div className="text-center mt-8">
              <p className="font-sans text-xs text-gray-medium flex items-center justify-center gap-2">
                <i className="ri-cursor-line text-gold" />
                Pasa el cursor sobre cada producto para navegar entre los colores disponibles
              </p>
            </div>
          </>
        )}

        {/* Grid — Ropa Deportiva */}
        {activeTab === 'deportiva' && (
          <>
            <div
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6"
              data-product-shop={true}
            >
              {sportswearCollection.map((p) => (
                <CarouselCard key={p.id} product={p} />
              ))}
            </div>
            <div className="text-center mt-8">
              <p className="font-sans text-xs text-gray-medium flex items-center justify-center gap-2">
                <i className="ri-cursor-line text-gold" />
                Pasa el cursor sobre cada producto para navegar entre los colores disponibles
              </p>
            </div>
          </>
        )}

        {/* Grid — Bodies */}
        {activeTab === 'bodies' && (
          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6"
            data-product-shop={true}
          >
            {bodyCollection.map((p) => (
              <BodyCard key={p.id} product={p} />
            ))}
          </div>
        )}

        {/* Grid — Sets */}
        {activeTab === 'sets' && (
          <>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-4xl mx-auto"
              data-product-shop={true}
            >
              {setsCollection.map((p) => (
                <SetCard key={p.id} product={p} />
              ))}
            </div>
            <div className="text-center mt-8">
              <p className="font-sans text-xs text-gray-medium flex items-center justify-center gap-2">
                <i className="ri-cursor-line text-gold" />
                Pasa el cursor sobre cada producto para navegar entre las vistas disponibles
              </p>
            </div>
          </>
        )}

        {/* Grid — Jeans */}
        {activeTab === 'jeans' && (
          <>
            <div
              className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-3xl mx-auto"
              data-product-shop={true}
            >
              {jeansCollection.map((p) => (
                <JeanCard key={p.id} product={p} />
              ))}
            </div>
            <div className="text-center mt-8">
              <p className="font-sans text-xs text-gray-medium flex items-center justify-center gap-2">
                <i className="ri-cursor-line text-gold" />
                Pasa el cursor sobre los productos con carrusel para ver todas las vistas
              </p>
            </div>
          </>
        )}

        {/* Grid — Crop Tops */}
        {activeTab === 'croptops' && (
          <>
            <div
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
              data-product-shop={true}
            >
              {cropTopCollection.map((p) => (
                <CropTopCard key={p.id} product={p} />
              ))}
            </div>
            <div className="text-center mt-8">
              <p className="font-sans text-xs text-gray-medium flex items-center justify-center gap-2">
                <i className="ri-cursor-line text-gold" />
                Pasa el cursor sobre los productos con carrusel para ver todas las vistas
              </p>
            </div>
          </>
        )}

        {/* Grid — Vestidos */}
        {activeTab === 'vestidos' && (
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
            data-product-shop={true}
          >
            {vestidosCollection.map((p) => (
              <VestidoCard key={p.id} product={p} />
            ))}
          </div>
        )}

      </div>
    </section>
  );
}
